import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../../hooks/useAuth';
import { usePermissions } from '../../hooks/usePermissions';
import { PERM } from '../../auth/permissions';
import { EASE_OUT, DURATION_NORMAL } from '@/lib/motion';

/** Where the plans actually live — the Plan sub-tab, scrolled to the cards. */
const PLANS_PATH = '/settings?tab=billing#tier-plans';

/**
 * "You're on Free, and here's the way up" — said on the Dashboard, once.
 *
 * Everyone who signs up lands on Free, including someone who clicked Join us
 * under the Pro card on the marketing site. Nothing on arrival said so, and
 * upgrading meant finding the gear, then Billing & Plan, then the Plan
 * sub-tab — three steps nobody guessed (Aleix, 2026-10-12).
 *
 * So the button goes straight to the plan cards rather than naming the path:
 * telling someone where to click is what made this unintuitive in the first
 * place. The path is still written out underneath, for anyone who wants to
 * know where they are going.
 *
 * Shown only to a member who can actually act on it (BILLING_MANAGE) — an
 * upgrade CTA for someone who cannot buy is a dead end — and dismissible,
 * remembered per user and workspace so it never nags.
 */
const FreePlanNotice: React.FC = () => {
  const { user, activeMembership } = useAuth();
  const { can } = usePermissions();

  const tenantId = activeMembership?.tenant?.id;
  const storageKey = `tereiko-free-plan-notice-dismissed:${user?.id ?? 'anon'}:${tenantId ?? 'none'}`;

  const [dismissed, setDismissed] = useState(() => {
    try {
      return localStorage.getItem(storageKey) === '1';
    } catch {
      // Private windows and blocked site data both throw — show the notice
      // rather than hiding it on a storage failure.
      return false;
    }
  });

  const dismiss = () => {
    try {
      localStorage.setItem(storageKey, '1');
    } catch {
      /* dismissal just won't persist; the notice still closes for this view */
    }
    setDismissed(true);
  };

  const onFreePlan = activeMembership?.tenant?.subscription_plan === 'free';
  if (!onFreePlan || dismissed || !can(PERM.BILLING_MANAGE)) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: DURATION_NORMAL, ease: EASE_OUT }}
      role="status"
      className="mb-6 overflow-hidden rounded-2xl border border-border-primary border-l-4 border-l-primary-500 bg-surface-elevated p-6 shadow-sm"
      data-testid="free-plan-notice"
    >
      <div className="flex items-start gap-4">
        <div className="min-w-0 flex-1">
          <h2 className="text-lg font-semibold leading-snug text-text-primary">
            You&apos;re on the Free plan — no cost, no card, no time limit
          </h2>
          <p className="mt-2 max-w-2xl text-[15px] leading-relaxed text-text-secondary">
            Build courses and invite your first learners straight away. When you need more
            seats or the features on the paid plans, you can move up at any time.
          </p>

          <div className="mt-5 flex flex-wrap items-center gap-x-4 gap-y-2">
            <Link to={PLANS_PATH} className="btn btn-primary">
              See the plans
            </Link>
            <span className="text-sm text-text-tertiary">
              Also under Settings → Billing &amp; Plan → Plans
            </span>
          </div>
        </div>

        <button
          type="button"
          onClick={dismiss}
          aria-label="Dismiss"
          title="Dismiss"
          className="focus-ring -mr-1 -mt-1 flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-text-tertiary transition-colors hover:bg-text-tertiary/10 hover:text-text-secondary"
        >
          <XMarkIcon aria-hidden="true" className="h-4 w-4" />
        </button>
      </div>
    </motion.div>
  );
};

export default FreePlanNotice;
